import React, { useState, useEffect, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, themeQuartz } from "ag-grid-community";

import { toast } from "react-toastify";
import axiosInstance from "../../../common/axiosInstance";
import { useOrgUsersStore, WashroomAuditType } from "../../../store/store";
import { useWashroomAuditStore } from "../../../store/store";
import { usePremisesStore } from "../../../store/store";
import { useNavigate } from "react-router-dom";
import images from "../../../assets/images";
import { getAllTypeList } from "../../../service/api";
import { WashroomTypeListType } from "../../../types/api";

interface Washroom {
  _id: string;
  premiseId: string;
  washroomName: string;
  type: string;
  floor: string;
  assignedStaff: string;
  createdDate: string;
  tenantId: string;
  createdAt: string;
}

interface Checklist {
  _id: string;
  name: string;
  premisesId: string;
  tenantId: string;
}

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const AuditHistory: React.FC = () => {
  const [washrooms, setWashrooms] = useState<Washroom[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const [filteredStaff, setFilteredStaff] = useState<any[]>([]);
  const [washroomTypeList, setWashroomTypeList] = useState<
    WashroomTypeListType[]
  >([]);
  const { loading, error, fetchAudits, audits } = useWashroomAuditStore();
  const { allPremises, fetchPremises } = usePremisesStore();
  const [auditList, setAuditList] = useState<WashroomAuditType[]>([]);
  const navigate = useNavigate();

  const fetchWashrooms = async () => {
    try {
      const response = await axiosInstance.get(`/api/washroom`);
      setWashrooms(response.data.message || []);
    } catch (err) {
      console.error("Error fetching washrooms:", err);
      toast.error("Failed to fetch washroom data");
    }
  };

  const fetchChecklists = async () => {
    try {
      const response = await axiosInstance.get(
        `/api/washroom-checklist-config`
      );
      if (response.status === 200) {
        setChecklists(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching checklists:", error);
      toast.error("Failed to fetch checklists");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchAudits(),
          fetchOrgUsers(),
          fetchPremises(),
          fetchWashrooms(),
          fetchChecklists(),
        ]);
      } catch (err) {
        console.error("Error fetching initial data:", err);
        toast.error("Failed to fetch initial data");
      }
    };
    fetchData();
  }, [fetchAudits, fetchOrgUsers]);

  useEffect(() => {
    if (audits.length > 0) {
      setAuditList(audits.filter((audit) => audit.status === "completed"));
    }
  }, [audits]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await getAllTypeList();
        if (res.data?.data && Array.isArray(res.data.data)) {
          setWashroomTypeList(res.data.data);
        } else {
          setWashroomTypeList([]);
        }
      } catch (error) {
        console.error("Failed to fetch washroom types:", error);
      }
    };
    fetchTypes();
  }, []);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "Washroom Name",
        field: "washroomId",
        flex: 1,
        sortable: true,
        valueGetter: (params) => {
          const washroom = washrooms.find(
            (w) => w._id === params.data.washroomId,
          );
          return washroom ? washroom.washroomName : "N/A";
        },
      },
      {
        headerName: "Washroom Type",
        field: "washroomId",
        flex: 1,
        valueGetter: (params) => {
          const washroomTypeId = params.data?.washroomDetails.type;
          const washroom = washroomTypeList.find(
            (w) => w._id === washroomTypeId,
          );
          return washroom ? washroom.typeName : "N/A";
        },
      },
      {
        headerName: "Floor",
        field: "washroomId",
        flex: 0.5,
        valueGetter: (params) => {
          const washroom = washrooms.find(
            (w) => w._id === params.data.washroomId,
          );
          return washroom ? washroom.floor : "N/A";
        },
      },
      {
        headerName: "Checklist",
        field: "checkListId",
        flex: 0.8,
        valueGetter: (params) => {
          const checklist = checklists.find(
            (c) => c._id === params.data.checkListId,
          );
          return checklist ? checklist.name : params.data.checkListId;
        },
      },
      {
        headerName: "Premises",
        field: "premisesId",
        flex: 0.8,
        valueGetter: (params) => {
          const premise = allPremises.find(
            (p) => p.id === params.data.premisesId,
          );
          return premise ? premise.premisesName : params.data.premisesId;
        },
      },
      {
        headerName: "Audit Date",
        field: "auditDate",
        flex: 0.7,
        sortable: true,
        valueFormatter: (params) => {
          if (!params.value) return "";
          const date = new Date(params.value);
          if (isNaN(date.getTime())) return params.value;
          return date.toLocaleDateString();
        },
      },
      {
        headerName: "Status",
        field: "status",
        flex: 0.8,
        cellRenderer: (params: any) => {
          const status = params.value?.toLowerCase();
          return (
            <div
              className={`px-2 py-1 rounded-full text-center text-sm ${status === "completed"
                  ? "bg-green-100 text-green-800"
                  : status === "pending"
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-gray-100 text-gray-800"
                }`}
            >
              {params.value}
            </div>
          );
        },
      },
      {
        headerName: "Actions",
        flex: 0.5,
        cellRenderer: (params: any) => (
          <div className="flex gap-x-1">
            <button
              className="p-1 hover:bg-red-100 rounded"
              onClick={() =>
                navigate(`/washroom-management/audits/view/${params.data._id}`)
              }
            >
              <img
                className="iconTd w-5 h-5"
                src={images.viewIcon}
                alt="View"
              />
            </button>
          </div>
        ),
      },
    ],
    [navigate, washrooms, checklists, allPremises, washroomTypeList],
  );
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      flex: 1,
    }),
    [],
  );

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-100vh">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Audit History</h1>
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-white text-[#0fa9a6] !border !border-[#0fa9a6] px-4 py-2 rounded-full hover:!bg-[#0fa9a6] hover:!border-teal-700 hover:text-white transition"
            onClick={() => navigate("/washroom-management/audits")}
          >
            BACK
          </button>
        </div>
        <div className="ag-theme-alpine w-full h-[calc(100vh-200px)]">
          <AgGridReact
            rowData={auditList}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            theme={gridTheme}
            headerHeight={65}
            rowHeight={50}
            pagination={true}
            paginationPageSize={10}
            enableCellTextSelection={true}
            suppressCellFocus={true}
            domLayout="autoHeight"
          />
        </div>
      </div>
    </>
  );
};

export default AuditHistory;
